import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { scrollToSection } from "@/lib/scrollTo";

const LINKS = [
  { to: "/solutions", label: "Solutions" },
  { to: "/applications", label: "Applications" },
  { to: "/products", label: "Products" },
  { to: "/technology", label: "Technology" },
];

const COMPANY_LINKS = [
  { to: "/about", label: "About" },
  { to: "/pilot", label: "Pilot Program" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const requestAssessment = () => {
    setOpen(false);
    if (location.pathname === "/") {
      scrollToSection("#contact");
    } else {
      navigate("/#contact");
    }
  };

  return (
    <header data-testid="navbar" className="fixed top-[31px] sm:top-6 lg:top-10 left-4 sm:left-6 lg:left-[264px] right-4 sm:right-6 lg:right-[264px] z-50">
      <div className="flex flex-col items-center sm:items-stretch">
        <div className="flex w-[87%] sm:w-full items-center justify-between bg-white rounded-full shadow-[0_10px_40px_-8px_rgba(17,24,34,0.18)] pl-5 sm:pl-6 pr-2.5 h-[60px] lg:h-[65px]">
        <Link to="/" data-testid="navbar-logo" className="font-heading font-semibold text-lg tracking-tight text-ink shrink-0">
          Off-Grid Hydrogen
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`navbar-link-${l.label.toLowerCase()}`}
              className="text-sm text-inkSoft hover:text-teal transition-colors duration-300"
            >
              {l.label}
            </Link>
          ))}

          <div
            className="relative"
            onMouseEnter={() => setCompanyOpen(true)}
            onMouseLeave={() => setCompanyOpen(false)}
          >
            <button
              type="button"
              data-testid="navbar-link-company"
              onClick={() => setCompanyOpen((v) => !v)}
              className="flex items-center gap-1 text-sm text-inkSoft hover:text-teal transition-colors duration-300"
            >
              Company
              <ChevronDown size={13} className={`transition-transform duration-300 ${companyOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {companyOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.18 }}
                  data-testid="navbar-company-dropdown"
                  className="absolute left-0 top-[calc(100%+14px)] min-w-[180px] rounded-2xl bg-white shadow-[0_10px_40px_-8px_rgba(17,24,34,0.18)] p-2"
                >
                  {COMPANY_LINKS.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      onClick={() => setCompanyOpen(false)}
                      data-testid={`navbar-link-company-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="block rounded-xl px-4 py-2.5 text-sm text-inkSoft hover:bg-baseLight hover:text-teal transition-colors duration-300"
                    >
                      {l.label}
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </nav>

        <button
          data-testid="navbar-cta-desktop"
          onClick={requestAssessment}
          className="hidden lg:flex h-10 items-center text-sm font-medium bg-teal text-white rounded-full px-5 hover:bg-tealHover transition-all duration-300"
        >
          Request Assessment
        </button>

        <button
          data-testid="navbar-mobile-toggle"
          className="lg:hidden h-10 w-10 rounded-full bg-baseLight flex items-center justify-center text-ink"
          onClick={() => setOpen(!open)}
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
        </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="w-[87%] sm:w-full lg:hidden bg-white rounded-[23px] shadow-[0_10px_40px_-8px_rgba(17,24,34,0.18)] overflow-hidden mt-3"
          >
            <div className="flex flex-col px-6 py-6 gap-5">
              {LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`navbar-mobile-link-${l.label.toLowerCase()}`}
                  className="text-left text-base text-inkSoft"
                >
                  {l.label}
                </Link>
              ))}
              <div className="h-px bg-borderLight" />
              {COMPANY_LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`navbar-mobile-link-company-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-left text-base text-inkSoft"
                >
                  {l.label}
                </Link>
              ))}
              <button
                data-testid="navbar-cta-mobile"
                onClick={requestAssessment}
                className="mt-2 h-10 flex items-center justify-center text-sm font-medium bg-teal text-white rounded-full px-5 text-center"
              >
                Request Assessment
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </header>
  );
};
